import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/sections";
import { toast, Toaster } from "sonner";
import { X, MessageCircle, ChevronLeft, ChevronDown } from "lucide-react";

const WHATSAPP_NUMBER = "919822580152";
const WA_PREFIX = `https://wa.me/${WHATSAPP_NUMBER}?text=`;

export default function GalleryBuy() {
  const [artworks, setArtworks] = useState([]);
  const [artistBios, setArtistBios] = useState({}); // name_lc -> artist doc
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const location = useLocation();
  const sectionRefs = useRef({}); // artist name -> DOM node

  useEffect(() => {
    Promise.all([api.get("/artworks"), api.get("/artists")])
      .then(([aw, ar]) => {
        setArtworks(aw.data);
        const map = {};
        for (const a of ar.data) map[(a.name || "").toLowerCase()] = a;
        setArtistBios(map);
      })
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const a of artworks) {
      const k = a.artist || "Unattributed";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(a);
    }
    // Strict alphabetical by artist name
    const keys = [...map.keys()].sort((a, b) => a.localeCompare(b));
    return keys.map((artist) => ({ artist, items: map.get(artist) }));
  }, [artworks]);

  // If we arrived from /artists with state.open = "<artist name>", expand THAT artist
  // and scroll to it. Otherwise fall back to expanding the first artist.
  useEffect(() => {
    if (!grouped.length || expanded.size > 0) return;
    const requested = location.state?.open;
    const match = requested && grouped.find(g => g.artist.toLowerCase() === requested.toLowerCase());
    if (match) {
      setExpanded(new Set([match.artist]));
      // Give the DOM one frame to render the expanded section, then scroll
      requestAnimationFrame(() => {
        const el = sectionRefs.current[match.artist];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else {
      setExpanded(new Set([grouped[0].artist]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped.length]);

  const toggle = (artist) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(artist)) next.delete(artist); else next.add(artist);
      return next;
    });
  };

  return (
    <div data-testid="gallery-buy-page" className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 lg:py-24">
      <Toaster position="top-center" richColors />
      <Link to="/gallery" className="inline-flex items-center gap-1 text-sm link-hover" data-testid="buy-back">
        <ChevronLeft size={16}/> Back to Gallery
      </Link>

      <div className="mt-8 overline">Buy Art</div>
      <h1 className="display text-5xl md:text-7xl mt-6">Take a piece <em className="italic text-terra">home.</em></h1>
      <p className="mt-6 max-w-2xl text-muted2 text-base leading-relaxed">
        A rotating selection of paintings, works on paper and studio ceramics from Carpe Diem&apos;s in-house collection
        and visiting artists. Pay online and we&apos;ll email you collection details, or WhatsApp us first if you&apos;d
        prefer to chat.
      </p>

      {loading ? (
        <div className="mt-20 text-muted2">Loading…</div>
      ) : artworks.length === 0 ? (
        <div className="mt-20 py-24 text-center">
          <div className="font-serif text-3xl">Nothing on sale right now.</div>
          <p className="text-muted2 mt-3 text-sm">Please check back soon or write to us at info@carpediemgoa.com.</p>
        </div>
      ) : (
        <div className="mt-16 space-y-6">
          {grouped.map(({ artist, items }) => {
            const isOpen = expanded.has(artist);
            const slug = artist.replace(/\s+/g, '-').toLowerCase();
            return (
              <section key={artist} ref={(el) => { sectionRefs.current[artist] = el; }} id={`artist-section-${slug}`} data-testid={`artist-section-${slug}`} className="border border-soft scroll-mt-24">
                <button
                  onClick={() => toggle(artist)}
                  aria-expanded={isOpen}
                  data-testid={`artist-toggle-${slug}`}
                  className="w-full flex items-center justify-between gap-6 px-6 md:px-8 py-6 text-left hover:bg-cream-2 transition-colors"
                >
                  <div className="flex items-baseline gap-4">
                    <div className="overline">Artist</div>
                    <h2 className="display text-2xl md:text-4xl">{artist}</h2>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs text-muted2">{items.length} work{items.length > 1 ? "s" : ""}</span>
                    <ChevronDown size={20} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 md:px-8 pb-10 pt-2">
                    <div className="hairline mb-8" />
                    {(() => {
                      const bio = artistBios[artist.toLowerCase()];
                      if (!bio || (!bio.bio && !bio.portrait_url && !bio.website && !bio.instagram && !(bio.past_shows_at_carpe_diem || []).length)) return null;
                      const shows = (bio.past_shows_at_carpe_diem || []).filter(s => s && (s.title || s.year));
                      return (
                        <div className="mb-10" data-testid={`artist-bio-${slug}`}>
                          <div className="flex flex-col md:flex-row gap-6 items-start">
                          {bio.portrait_url && (
                            <img src={bio.portrait_url} alt={artist} className="w-28 h-28 object-cover border border-soft shrink-0" />
                          )}
                          <div className="flex-1">
                            {bio.bio && <p className="text-sm leading-relaxed text-charcoal whitespace-pre-line">{bio.bio}</p>}
                            <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted2">
                              {bio.website && <a href={bio.website} target="_blank" rel="noreferrer" className="link-hover">Website ↗</a>}
                              {bio.instagram && <a href={bio.instagram.startsWith("http") ? bio.instagram : `https://instagram.com/${bio.instagram.replace("@","")}`} target="_blank" rel="noreferrer" className="link-hover">Instagram ↗</a>}
                              {(bio.cv_text || bio.cv_url) && (
                                <button type="button" onClick={() => setSelected({ __artistCv: bio })} className="link-hover underline underline-offset-4" data-testid={`artist-cv-open-${slug}`}>Read full CV ↗</button>
                              )}
                            </div>
                          </div>
                          </div>
                          {shows.length > 0 && (
                            <div className="mt-8 pt-6 border-t border-soft" data-testid={`past-shows-${slug}`}>
                              <div className="overline mb-3 flex items-center gap-3">
                                Past exhibitions at Carpe Diem
                                <span className="text-muted2 normal-case tracking-normal">· {shows.length}</span>
                              </div>
                              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
                                {shows.map((s, i) => (
                                  <li key={i} className="flex gap-3 items-baseline text-sm">
                                    <span className="font-serif text-terracotta w-12 shrink-0">{s.year}</span>
                                    <span className="italic font-serif text-base">{s.title}</span>
                                    {s.notes && <span className="text-muted2 text-xs">· {s.notes}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                      {items.map(a => (
                        <button
                          key={a.id}
                          onClick={() => setSelected(a)}
                          className="text-left group"
                          data-testid={`artwork-${a.id.slice(0,8)}`}
                        >
                          <div className="aspect-[4/5] overflow-hidden bg-cream-2 border border-soft relative flex items-center justify-center p-2">
                            <img
                              src={a.image_url}
                              alt={a.title}
                              loading="lazy"
                              className="reveal-img max-w-full max-h-full w-auto h-auto object-contain"
                            />
                            {!a.is_available && (
                              <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest px-2 py-1" style={{background:"#1C1C19",color:"#F9F8F6"}}>Sold</div>
                            )}
                          </div>
                          <div className="pt-4 flex items-baseline justify-between gap-4">
                            <div className="flex-1">
                              <h3 className="font-serif text-2xl italic leading-tight">{a.title}</h3>
                              <div className="text-xs text-muted2 mt-2">{a.medium}</div>
                              <div className="text-xs text-muted2">{a.size}{a.year ? ` · ${a.year}` : ""}</div>
                            </div>
                            <div className="font-serif text-xl">{formatINR(a.price_inr)}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {selected && selected.__artistCv && <ArtistCvModal artist={selected.__artistCv} onClose={() => setSelected(null)} />}
      {selected && !selected.__artistCv && <ArtworkModal artwork={selected} onClose={() => setSelected(null)} onSold={(id) => setArtworks(xs => xs.map(x => x.id === id ? {...x, is_available: false} : x))} />}
    </div>
  );
}


function ArtistCvModal({ artist, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 md:p-10" onClick={onClose}>
      <div className="bg-cream max-w-2xl w-full max-h-[85vh] overflow-y-auto border border-soft" onClick={(e) => e.stopPropagation()} data-testid="artist-cv-modal">
        <div className="flex items-center justify-between p-5 border-b border-soft sticky top-0 bg-cream">
          <div>
            <div className="overline">Artist</div>
            <div className="font-serif text-2xl">{artist.name}</div>
          </div>
          <button onClick={onClose} data-testid="artist-cv-close"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          {artist.portrait_url && <img src={artist.portrait_url} alt={artist.name} className="w-32 h-32 object-cover border border-soft" />}
          {artist.bio && (
            <div>
              <div className="overline mb-2">About</div>
              <p className="text-sm leading-relaxed whitespace-pre-line">{artist.bio}</p>
            </div>
          )}
          {artist.cv_text && (
            <div>
              <div className="overline mb-2">CV</div>
              <pre className="text-sm leading-relaxed whitespace-pre-wrap font-serif">{artist.cv_text}</pre>
            </div>
          )}
          {artist.cv_url && (
            <a href={artist.cv_url} target="_blank" rel="noreferrer" className="btn-ghost inline-flex" data-testid="artist-cv-download">Download CV (PDF) ↗</a>
          )}
          <div className="flex flex-wrap gap-4 text-xs text-muted2 pt-4 border-t border-soft">
            {artist.website && <a href={artist.website} target="_blank" rel="noreferrer" className="link-hover">Website ↗</a>}
            {artist.instagram && <a href={artist.instagram.startsWith("http") ? artist.instagram : `https://instagram.com/${artist.instagram.replace("@","")}`} target="_blank" rel="noreferrer" className="link-hover">Instagram ↗</a>}
          </div>
        </div>
      </div>
    </div>
  );
}


function ArtworkModal({ artwork, onClose, onSold }) {
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const a = artwork;

  const buy = async (e) => {
    e.preventDefault();
    if (!a.is_available) return toast.error("This piece has just been sold");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/artworks/${a.id}/purchase`, buyer);
      if (data.is_mock || data.amount_inr === 0) {
        await api.post("/bookings/verify", {
          booking_id: data.booking_id,
          razorpay_order_id: data.razorpay_order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: "mock_signature",
        });
        toast.success("Purchase confirmed");
        onSold(a.id);
        nav(`/confirmation/${data.booking_id}`);
        return;
      }
      if (!window.Razorpay) return toast.error("Razorpay SDK not loaded");
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        amount: data.amount_paise,
        currency: data.currency,
        name: "Carpe Diem Goa",
        description: `${a.title} — ${a.artist}`,
        order_id: data.razorpay_order_id,
        prefill: { name: buyer.name, email: buyer.email, contact: buyer.phone },
        theme: { color: "#C05C42" },
        handler: async (r) => {
          try {
            await api.post("/bookings/verify", {
              booking_id: data.booking_id,
              razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature: r.razorpay_signature,
            });
            toast.success("Payment confirmed");
            onSold(a.id);
            nav(`/confirmation/${data.booking_id}`);
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
          }
        },
        modal: { ondismiss: () => toast.message("Payment cancelled") },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Purchase failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const waLink = WA_PREFIX + encodeURIComponent(
    `Hi Carpe Diem — I'd like to enquire about "${a.title}" by ${a.artist} (${a.medium}, ${a.size}, ₹${a.price_inr.toLocaleString("en-IN")}).`
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 md:p-10" onClick={onClose}>
      <div className="bg-cream max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-soft grid md:grid-cols-2 gap-0" onClick={(e) => e.stopPropagation()} data-testid="artwork-modal">
        <div className="aspect-square md:aspect-auto md:h-full min-h-[380px] overflow-hidden bg-cream-2 flex items-center justify-center p-2">
          <img src={a.image_url} alt={a.title} className="max-w-full max-h-full w-auto h-auto object-contain" />
        </div>
        <div className="p-6 md:p-8 relative">
          <button onClick={onClose} className="absolute top-4 right-4" data-testid="modal-close"><X size={20}/></button>
          <div className="overline">{a.artist}</div>
          <h2 className="font-serif italic text-3xl md:text-4xl mt-3 leading-tight">{a.title}</h2>
          <dl className="mt-5 text-sm space-y-1 text-muted2">
            <div><span className="text-charcoal">Medium</span> · {a.medium}</div>
            <div><span className="text-charcoal">Size</span> · {a.size}</div>
            {a.year && <div><span className="text-charcoal">Year</span> · {a.year}</div>}
          </dl>
          {a.description && <p className="text-sm mt-5 leading-relaxed">{a.description}</p>}
          <div className="font-serif text-4xl mt-6">{formatINR(a.price_inr)}</div>

          {!a.is_available ? (
            <div className="mt-6 border border-soft p-4 text-sm">This piece has been sold. Please email us at info@carpediemgoa.com about similar work.</div>
          ) : (
            <>
              <form onSubmit={buy} className="mt-6 space-y-3" data-testid="artwork-buy-form">
                <input required placeholder="Your name" value={buyer.name} onChange={(e)=>setBuyer({...buyer,name:e.target.value})} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" data-testid="buyer-name" />
                <input required type="email" placeholder="Email" value={buyer.email} onChange={(e)=>setBuyer({...buyer,email:e.target.value})} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" data-testid="buyer-email" />
                <input required type="tel" placeholder="Phone (for collection)" value={buyer.phone} onChange={(e)=>setBuyer({...buyer,phone:e.target.value})} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" data-testid="buyer-phone" />
                <button disabled={submitting} type="submit" className="btn-primary w-full justify-center mt-2" data-testid="buyer-submit">
                  {submitting ? "Processing…" : `Pay ${formatINR(a.price_inr)}`}
                </button>
              </form>
              <div className="mt-3 text-xs text-muted2 leading-relaxed">
                After payment we&apos;ll email you a receipt and follow up with collection / delivery details. You may also
                collect the piece from Majorda during gallery hours.
              </div>
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="mt-4 btn-ghost w-full justify-center"
                data-testid="artwork-whatsapp"
                style={{borderColor:"#4A5D4E", color:"#4A5D4E"}}
              >
                <MessageCircle size={14}/> Chat with us on WhatsApp first
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
